export type TableName =
  "pages" | "services" | "plans" | "portfolio_items" | "case_studies" | "blog_categories" | "faqs" | "media";

export type FieldType =
  | "text"
  | "textarea"
  | "number"
  | "checkbox"
  | "select"
  | "list"
  | "json"
  | "date"
  | "url"
  | "image"
  | "relation";

export interface FieldConfig {
  name: string;
  label: string;
  type?: FieldType;
  required?: boolean;
  hint?: string;
  options?: { label: string; value: string }[];
  relation?: "pages" | "portfolio_items";
}

export interface ContentConfig {
  section: string;
  title: string;
  description: string;
  table: TableName;
  titleField: string;
  subtitleField?: string;
  hasPublished?: boolean;
  orderField?: string;
  fields: FieldConfig[];
}

const publishedField: FieldConfig = {
  name: "is_published",
  label: "Publicado",
  type: "checkbox",
};

const seoFields: FieldConfig[] = [
  { name: "seo_title", label: "Titulo SEO" },
  { name: "seo_description", label: "Descripcion SEO", type: "textarea" },
];

export const contentConfigs: Record<string, ContentConfig> = {
  paginas: {
    section: "paginas",
    title: "Paginas",
    description: "Contenido estructurado y metadata SEO de paginas fijas.",
    table: "pages",
    titleField: "title",
    subtitleField: "slug",
    hasPublished: true,
    fields: [
      { name: "title", label: "Titulo", required: true },
      { name: "slug", label: "Slug", required: true, hint: "Ejemplo: nosotros" },
      { name: "canonical_url", label: "Canonical", type: "url" },
      { name: "hero_image_url", label: "Imagen principal", type: "image" },
      { name: "og_image_url", label: "Imagen OG", type: "image" },
      { name: "page_date", label: "Fecha", type: "date" },
      { name: "content", label: "Contenido JSON", type: "json", hint: "Bloques estructurados en JSON." },
      ...seoFields,
      publishedField,
    ],
  },
  servicios: {
    section: "servicios",
    title: "Servicios",
    description: "Servicios publicados en la web publica.",
    table: "services",
    titleField: "name",
    subtitleField: "summary",
    hasPublished: true,
    orderField: "sort_order",
    fields: [
      { name: "name", label: "Nombre", required: true },
      { name: "slug", label: "Slug", required: true },
      {
        name: "category",
        label: "Categoria",
        type: "select",
        options: [
          { label: "Web", value: "Web" },
          { label: "Tiendas online", value: "Tiendas online" },
          { label: "Marketing digital", value: "Marketing digital" },
          { label: "SEO", value: "SEO" },
        ],
      },
      { name: "summary", label: "Descripcion corta", required: true, type: "textarea" },
      { name: "long_description", label: "Descripcion larga", type: "textarea" },
      { name: "benefits", label: "Beneficios", type: "list", hint: "Un beneficio por linea." },
      { name: "image_url", label: "Imagen", type: "image" },
      { name: "icon", label: "Icono" },
      { name: "cta_label", label: "Texto CTA" },
      { name: "cta_url", label: "URL CTA", type: "url" },
      { name: "sort_order", label: "Orden", type: "number" },
      ...seoFields,
      publishedField,
    ],
  },
  planes: {
    section: "planes",
    title: "Planes",
    description: "Ofertas de desarrollo web, SaaS de tiendas y visibilidad en el directorio.",
    table: "plans",
    titleField: "name",
    subtitleField: "slug",
    hasPublished: true,
    orderField: "sort_order",
    fields: [
      { name: "name", label: "Nombre", required: true },
      { name: "slug", label: "Slug", required: true },
      {
        name: "offer_type",
        label: "Tipo de oferta",
        type: "select",
        hint: "Los planes de promoción aparecen en la sección exclusiva del directorio en /planes/.",
        options: [
          { label: "Desarrollo web personalizado", value: "custom_web" },
          { label: "SaaS de tiendas", value: "saas" },
          { label: "Promoción del directorio", value: "directory" },
        ],
      },
      { name: "price_amount", label: "Precio", type: "number" },
      {
        name: "billing_period",
        label: "Periodo",
        type: "select",
        options: [
          { label: "Unico", value: "one_time" },
          { label: "Mensual", value: "monthly" },
          { label: "Anual", value: "yearly" },
        ],
      },
      { name: "summary", label: "Resumen", type: "textarea" },
      { name: "target_audience", label: "Publico objetivo", type: "textarea" },
      { name: "delivery_time", label: "Tiempo de entrega" },
      { name: "renewal_terms", label: "Renovaciones", type: "textarea" },
      { name: "features", label: "Funcionalidades", type: "list", hint: "Una funcionalidad por linea." },
      {
        name: "directory_priority",
        label: "Prioridad en resultados",
        type: "select",
        hint: "Define cómo se ordenan las fichas de este plan. El monto negociado no altera la prioridad; usa Básico para cortesías sin promoción.",
        options: [
          { label: "Básico · orden orgánico", value: "0" },
          { label: "Destacado · prioridad alta", value: "1" },
          { label: "Premium · máxima prioridad", value: "2" },
        ],
      },
      { name: "cta_label", label: "Texto CTA" },
      { name: "cta_url", label: "URL CTA", type: "url" },
      { name: "is_featured", label: "Destacado", type: "checkbox" },
      { name: "sort_order", label: "Orden", type: "number" },
      ...seoFields,
      publishedField,
    ],
  },
  portafolio: {
    section: "portafolio",
    title: "Portafolio",
    description: "Proyectos personalizados, separados del directorio SaaS.",
    table: "portfolio_items",
    titleField: "title",
    subtitleField: "summary",
    hasPublished: true,
    orderField: "sort_order",
    fields: [
      { name: "title", label: "Nombre", required: true },
      { name: "slug", label: "Slug", required: true },
      {
        name: "category",
        label: "Categoria",
        type: "select",
        options: [
          { label: "Web corporativa", value: "Web corporativa" },
          { label: "Belleza", value: "Belleza" },
          { label: "Moda y productos", value: "Moda y productos" },
          { label: "Boutique", value: "Boutique" },
          { label: "E-commerce", value: "E-commerce" },
        ],
      },
      { name: "summary", label: "Descripcion corta", required: true, type: "textarea" },
      { name: "description", label: "Descripcion JSON", type: "json" },
      { name: "cover_image_url", label: "Imagen principal", type: "image" },
      { name: "gallery", label: "Galeria JSON", type: "json" },
      { name: "features", label: "Funcionalidades", type: "list" },
      { name: "technologies", label: "Tecnologias", type: "list" },
      { name: "project_url", label: "URL real", type: "url" },
      { name: "case_url", label: "URL del caso", type: "url" },
      { name: "is_featured", label: "Destacado", type: "checkbox" },
      { name: "sort_order", label: "Orden", type: "number" },
      ...seoFields,
      publishedField,
    ],
  },
  casos: {
    section: "casos",
    title: "Casos",
    description: "Casos con problema, objetivo, solucion, proceso y resultado.",
    table: "case_studies",
    titleField: "title",
    subtitleField: "summary",
    hasPublished: true,
    fields: [
      { name: "title", label: "Titulo", required: true },
      { name: "slug", label: "Slug", required: true },
      {
        name: "portfolio_item_id",
        label: "Proyecto relacionado",
        type: "relation",
        relation: "portfolio_items",
      },
      { name: "subtitle", label: "Subtitulo", type: "textarea" },
      { name: "summary", label: "Resumen", required: true, type: "textarea" },
      { name: "cover_image_url", label: "Imagen de listado", type: "image" },
      { name: "hero_image_url", label: "Imagen hero", type: "image" },
      { name: "problem", label: "Problema", type: "textarea" },
      { name: "objective", label: "Objetivo", type: "textarea" },
      { name: "solution", label: "Solucion", type: "textarea" },
      { name: "result_text", label: "Resultado/entregable", type: "textarea" },
      { name: "process", label: "Proceso", type: "list" },
      { name: "technologies", label: "Tecnologias", type: "list" },
      { name: "gallery", label: "Galeria JSON", type: "json" },
      { name: "cta_label", label: "Texto CTA" },
      { name: "cta_url", label: "URL CTA", type: "url" },
      { name: "project_url", label: "URL del proyecto", type: "url" },
      publishedField,
    ],
  },
  categorias: {
    section: "categorias",
    title: "Categorias",
    description: "Categorias reutilizables del blog.",
    table: "blog_categories",
    titleField: "name",
    subtitleField: "slug",
    orderField: "slug",
    fields: [
      { name: "name", label: "Nombre", required: true },
      { name: "slug", label: "Slug", required: true },
    ],
  },
  faqs: {
    section: "faqs",
    title: "FAQs",
    description: "Preguntas frecuentes por pagina o generales.",
    table: "faqs",
    titleField: "question",
    subtitleField: "page_slug",
    hasPublished: true,
    orderField: "sort_order",
    fields: [
      { name: "question", label: "Pregunta", required: true, type: "textarea" },
      { name: "answer", label: "Respuesta", required: true, type: "textarea" },
      {
        name: "category",
        label: "Categoria",
        type: "select",
        options: [
          { label: "General", value: "General" },
          { label: "Planes", value: "Planes" },
          { label: "Servicios", value: "Servicios" },
          { label: "Directorio", value: "Directorio" },
        ],
      },
      { name: "page_slug", label: "Pagina asociada", type: "relation", relation: "pages" },
      { name: "sort_order", label: "Orden", type: "number" },
      publishedField,
    ],
  },
  media: {
    section: "media",
    title: "Media",
    description: "Archivos registrados desde Supabase Storage.",
    table: "media",
    titleField: "storage_path",
    subtitleField: "alt_text",
    fields: [
      { name: "storage_path", label: "Ruta Storage", required: true },
      { name: "public_url", label: "URL publica", type: "url" },
      { name: "file_name", label: "Nombre de archivo" },
      { name: "mime_type", label: "Tipo MIME" },
      { name: "size_bytes", label: "Tamano en bytes", type: "number" },
      { name: "alt_text", label: "Alt text", type: "textarea" },
      { name: "associated_table", label: "Tabla asociada" },
      { name: "associated_id", label: "ID asociado" },
      { name: "metadata", label: "Metadata JSON", type: "json" },
    ],
  },
};

export function getContentConfig(section: string): ContentConfig {
  return contentConfigs[section] ?? contentConfigs.paginas!;
}
